import P from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { CenteredContainer } from '@nerdwallet/currency';
import classNames from 'classnames';
import { compose } from 'redux';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import styles from './grid.module.less';
import Row from '../row/row';
import UniversalText from '../universal-text/universal-text';
import UniversalImage from '../universal-image/universal-image';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import UniversalButton from '../universal-button/universal-button';
import YouTubePlayer from '../../lib/youtube-player/youtube-player';
import Mp4VideoPlayer from '../../lib/mp4-video-player/mp4-video-player';
import SpotifyPlayer from '../../lib/spotify-player';
import SoundCloudPlayer from '../../lib/soundcloud-player';
import UniversalAccordion from '../universal-accordion/universal-accordion';
import { updateComponent } from '../../containers/builder/actions';
import { isInBuilder } from '../../lib/helpers';
import BainReferralBannerSC from '../bain-referral-banner-sc/bain-referral-banner-sc';
import AdvertiserDisclosureWrapper from '../advertiser-disclosure/advertiser-disclosure';

export const componentTypes = {
  TEXT: 'text',
  IMAGE: 'image',
  BUTTON: 'button',
  YOUTUBE: 'youtube',
  MP4VIDEO: 'mp4',
  SPOTIFY: 'spotify',
  SOUNDCLOUD: 'soundcloud',
  BAINREFERRALBANNER: 'bain referral banner',
  ADVERTISERDISCLOSURE: 'advertiser disclosure',
  ACCORDION: 'accordion',
};

const ItemComponent = ({
  type,
  text,
  image,
  button,
  youtube,
  mp4Video,
  spotify,
  soundcloud,
  bainReferralBanner,
  advertiserDisclosure,
  accordion,
  size,
}) => {
  switch (type) {
    case componentTypes.TEXT: {
      return <UniversalText {...text} outputSample viewPortSize={size} />;
    }
    case componentTypes.IMAGE: {
      return <UniversalImage {...image} outputSample />;
    }
    case componentTypes.YOUTUBE: {
      return <YouTubePlayer {...youtube} includeContainer />;
    }
    case componentTypes.MP4VIDEO: {
      return <Mp4VideoPlayer {...mp4Video} outputSample includeContainer />;
    }
    case componentTypes.SPOTIFY: {
      return <SpotifyPlayer {...spotify} outputSample includeContainer />;
    }
    case componentTypes.SOUNDCLOUD: {
      return <SoundCloudPlayer {...soundcloud} outputSample includeContainer />;
    }
    case componentTypes.BUTTON: {
      return <UniversalButton {...button} outputSample includeContainer />;
    }
    case componentTypes.BAINREFERRALBANNER: {
      return <BainReferralBannerSC {...bainReferralBanner} />;
    }
    case componentTypes.ADVERTISERDISCLOSURE: {
      return <AdvertiserDisclosureWrapper {...advertiserDisclosure} />;
    }
    case componentTypes.ACCORDION: {
      return <UniversalAccordion {...accordion} />;
    }
    default:
      return null;
  }
};

const ConnectedItemComponent = connect(
  createStructuredSelector({
    size: (state) => state?.mediaQuery,
  })
)(ItemComponent);

const ItemComponents = ({ components, outputDashedBorder }) => {
  const outputEmptyGridItemStyle = !components?.some((item) =>
    Object.prototype.hasOwnProperty.call(item, 'type')
  );

  return (
    <div
      className={classNames([
        { [styles.itemComponentBorder]: outputDashedBorder },
        { [styles.emptyGridItemStyle]: outputEmptyGridItemStyle },
      ])}
    >
      {components?.map((component) => (
        <ConnectedItemComponent key={component.$unique_key} {...component} />
      ))}
    </div>
  );
};

const GridItems = ({
  items,
  disableDefaultProps,
  isBuilderPage,
  outputGridItemIndicator,
  outputDashedBorder,
}) => {
  const itemContainerConfig = (item) => ({
    ...item?.itemContainer,
    alignment: {
      ...item?.itemContainer?.alignment,
      type: 'flex',
    },
  });

  return items.map((item, index) => (
    <UniversalContainer
      {...itemContainerConfig(item)}
      style={{ position: 'relative' }}
    >
      {outputGridItemIndicator && (
        <span data-testid="grid-indicator" className={styles.gridItemIndicator}>
          {index + 1}
        </span>
      )}
      <ItemComponents
        key={item.$unique_key}
        components={item?.itemComponents}
        disableDefaultProps={disableDefaultProps}
        isBuilderPage={isBuilderPage}
        outputDashedBorder={outputDashedBorder}
      />
    </UniversalContainer>
  ));
};

const defaultProps = {
  gridItems: [
    {
      $unique_key: new Date().getTime(),
    },
  ],
};

const Grid = ({
  container,
  title,
  subtitle,
  centeredInParentBackground,
  gridItems,
  columns,
  gap,
  centeredInParent,
  disableDefaultProps,
  updateComponentProps,
  untouched,
  selectedComponent,
  componentId,
  floatingGrid,
  floatTop,
  floatLeft,
  floatRight,
  floatBottom,
  floatIndex,
  history,
}) => {
  useEffect(() => {
    if (untouched && selectedComponent?.id === componentId) {
      updateComponentProps({
        ...selectedComponent,
        props: { ...defaultProps, untouched: false },
      });
    }
  }, [componentId, selectedComponent, untouched, updateComponentProps]);

  const isBuilderPage = isInBuilder(history?.location?.pathname);
  const outputFloatingGridTransparency = floatingGrid && !disableDefaultProps;
  const isGridInEditMode = selectedComponent?.id === componentId;
  const outputDashedBorder =
    !disableDefaultProps && isBuilderPage && isGridInEditMode;
  const outputGridItemIndicator =
    !disableDefaultProps && isBuilderPage && isGridInEditMode;

  const floatingGridStyle = floatingGrid
    ? {
        position: 'absolute',
        top: floatTop || 0,
        bottom: floatBottom || 0,
        left: floatLeft || 0,
        right: floatRight || 0,
        zIndex: floatIndex,
      }
    : null;

  const content = (
    <UniversalContainer
      {...container}
      style={{ ...(floatingGrid && floatingGridStyle) }}
      data-testid="grid-root-container"
    >
      {title && <UniversalText {...title} data-testid="grid-title" />}
      {subtitle && (
        <UniversalText
          {...subtitle}
          defaultOptions={{ text: 'text' }}
          data-testid="grid-subtitle"
        />
      )}

      <div>
        <Row
          columns={columns}
          style={{ ...(gap && { gap: `${gap}px` }) }}
          className={classNames([
            {
              [styles.itemComponentTransparency]:
                outputFloatingGridTransparency,
            },
          ])}
        >
          <GridItems
            outputGridItemIndicator={outputGridItemIndicator}
            outputDashedBorder={outputDashedBorder}
            isBuilderPage={isBuilderPage}
            items={gridItems}
            disableDefaultProps={disableDefaultProps}
          />
        </Row>
      </div>
    </UniversalContainer>
  );

  if (centeredInParent) {
    return (
      <UniversalContainer {...centeredInParentBackground}>
        <CenteredContainer>{content}</CenteredContainer>
      </UniversalContainer>
    );
  }

  return content;
};

Grid.displayName = 'Grid';

Grid.propTypes = {
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  centeredInParentBackground: P.object,
  columns: P.string,
  gridItems: P.array,
  gap: P.array,
  centeredInParent: P.bool,
  disableDefaultProps: P.bool,
  untouched: P.bool,
  selectedComponent: P.object,
  componentId: P.string,
  flatOutline: P.object,
  updateComponentProps: P.func,
  floatingGrid: P.bool,
  floatTop: P.string,
  floatLeft: P.string,
  floatRight: P.string,
  floatBottom: P.string,
  floatIndex: P.number,
  history: P.object,
};

Grid.defaultProps = {
  container: {},
  centeredInParentBackground: {},
  columns: '1',
  gridItems: [],
  gap: '0',
  centeredInParent: false,
  disableDefaultProps: false,
  floatingGrid: false,
  floatTop: '0',
  floatLeft: '0',
  floatRight: '0',
  floatBottom: '0',
  floatIndex: 1,
};

GridItems.propTypes = {
  items: P.array,
  disableDefaultProps: P.bool,
  isBuilderPage: P.bool,
  outputGridItemIndicator: P.bool,
  outputDashedBorder: P.bool,
};

GridItems.defaultProps = {
  items: [],
};

ItemComponents.propTypes = {
  components: P.array,
  outputDashedBorder: P.bool,
};

ItemComponent.defaultProps = {
  ItemComponents: [],
};

ItemComponent.propTypes = {
  type: P.string,
  text: UniversalText.propTypes,
  image: UniversalImage.propTypes,
  button: UniversalButton.propTypes,
  youtube: YouTubePlayer.propTypes,
  mp4Video: Mp4VideoPlayer.propTypes,
  spotify: SpotifyPlayer.propTypes,
  soundcloud: SoundCloudPlayer.propTypes,
  bainReferralBanner: BainReferralBannerSC.propTypes,
  advertiserDisclosure: AdvertiserDisclosureWrapper.propTypes,
  accordion: UniversalAccordion.propTypes,
  size: P.string,
};

ItemComponent.defaultProps = {
  type: 'none',
  text: {},
  image: {},
  button: {},
  youtube: {},
  mp4Video: {},
  spotify: {},
  soundcloud: {},
  bainReferralBanner: {},
  advertiserDisclosure: {},
  accordion: {},
};

Grid.getEditingConfig = (componentDoc) => {
  // make sure all gridItems has '$unique_key'
  const filteredGridItems = componentDoc?.props?.gridItems?.filter(
    (gridItem) => '$unique_key' in gridItem
  );

  const componentDocument = {
    ...componentDoc,
    // update componentDocument with sanitized gridItems
    props: { ...componentDoc?.props, gridItems: filteredGridItems },
  };

  const centeredInParentBackgroundInputConfig = componentDocument?.props
    ?.centeredInParent
    ? [
        ...UniversalContainer.getEditingConfig(
          componentDocument,
          'Centered Parent Background',
          ['backgroundColor', 'backgroundImage'],
          'centeredInParentBackground',
          {
            disableGroup: true,
            customLabels: {
              background: 'Centered In Parent Background',
            },
          }
        ),
      ]
    : [];

  const floatingGridControls = componentDocument?.props?.floatingGrid
    ? [
        group('Floating Grid Options', [
          stringInputSpec('floatTop', 'Float Top', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatBottom', 'Float Bottom', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatLeft', ' Float Left', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatRight', ' Float Right', {
            help: 'Defaults to "0"',
          }),
          numberInputSpec('floatIndex', 'Float Index'),
        ]),
      ]
    : [];

  return [
    boolCheckboxSpec(
      'disableDefaultProps',
      'Disable Grid Outline/Transparency?'
    ),
    stringSelectSpec(
      'columns',
      'Number of columns',
      [
        {
          value: '',
          label: 'Auto',
        },
        {
          value: '1',
          label: '1',
        },
        {
          value: '2',
          label: '2',
        },
        {
          value: '3',
          label: '3',
        },
        {
          value: '4',
          label: '4',
        },
      ],
      {
        help: 'Number of columns in layout on desktop',
      }
    ),

    boolCheckboxSpec('floatingGrid', 'Floating Grid?'),
    ...floatingGridControls,

    group('Grid Container', [
      numberInputSpec('gap', 'Gap', {
        help: 'Space between grid items in pixels. Defaults to "0" ',
      }),
      boolCheckboxSpec('centeredInParent', 'Centered In Parent'),
      ...centeredInParentBackgroundInputConfig,
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Grid Container',
        [
          'backgroundColor',
          'backgroundImage',
          'border',
          'margin',
          'padding',
          'hide',
          'jumpLinkId',
        ],
        'container',
        {
          disableGroup: true,
        }
      ),
    ]),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'width',
        'margin',
        'padding',
        'textChronicle',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'width',
        'margin',
        'padding',
        'textChronicle',
        'textColor',
        'textSize',
        'textType',
      ],
      'subtitle',
      { text: 'text' }
    ),
    repeatable('gridItems', '', 'Grid Item', (item) => [
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Grid Item Container',
        [
          'backgroundColor',
          'backgroundImage',
          'border',
          'margin',
          'padding',
          'width',
          'alignment',
          'hide',
        ],
        `gridItems.${item}.itemContainer`
      ),
      repeatable('itemComponents', '', 'Component', (component) => {
        const componentType =
          componentDocument?.props?.gridItems?.[item]?.itemComponents?.[
            component
          ]?.type;

        const textInputConfig =
          componentType === componentTypes.TEXT
            ? [
                ...UniversalText.getEditingConfig(
                  componentDocument,
                  'Text Options',
                  [
                    'fontWeight',
                    'groupButton',
                    'width',
                    'margin',
                    'padding',
                    'textChronicle',
                    'textColor',
                    'textSize',
                    'textType',
                    'placement',
                  ],
                  `gridItems.${item}.itemComponents.${component}.text`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const imageInputConfig =
          componentType === componentTypes.IMAGE
            ? [
                ...UniversalImage.getEditingConfig(
                  componentDocument,
                  'Image Options',
                  ['border', 'margin', 'padding'],
                  `gridItems.${item}.itemComponents.${component}.image`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const youtubeInputConfig =
          componentType === componentTypes.YOUTUBE
            ? [
                ...YouTubePlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.youtube`,
                  true
                ),
              ]
            : [];

        const mp4VideoInputConfig =
          componentType === componentTypes.MP4VIDEO
            ? [
                ...Mp4VideoPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.mp4Video`,
                  true
                ),
              ]
            : [];

        const spotifyInputConfig =
          componentType === componentTypes.SPOTIFY
            ? [
                ...SpotifyPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.spotify`,
                  true
                ),
              ]
            : [];

        const soundCloudInputConfig =
          componentType === componentTypes.SOUNDCLOUD
            ? [
                ...SoundCloudPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.soundcloud`,
                  true
                ),
              ]
            : [];

        const buttonInputConfig =
          componentType === componentTypes.BUTTON
            ? [
                ...UniversalButton.getEditingConfig(
                  componentDocument,
                  'Button Options',
                  [],
                  `gridItems.${item}.itemComponents.${component}.button`,
                  { disableGroup: true, includeContainer: true }
                ),
              ]
            : [];

        const bainReferralBannerConfig =
          componentType === componentTypes.BAINREFERRALBANNER
            ? [
                ...BainReferralBannerSC.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.bainReferralBanner`
                ),
              ]
            : [];
        const advertiserDisclosureConfig =
          componentType === componentTypes.ADVERTISERDISCLOSURE
            ? [
                ...AdvertiserDisclosureWrapper.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.advertiserDisclosure`
                ),
              ]
            : [];
        const accordionConfig =
          componentType === componentTypes.ACCORDION
            ? [
                ...UniversalAccordion.getEditingConfig(
                  componentDocument,
                  'Accordion',
                  `gridItems.${item}.itemComponents.${component}.accordion`,
                  { disableGroup: true, disablePreview: true }
                ),
              ]
            : [];

        return [
          !componentType
            ? stringSelectSpec('type', 'Component List', [
                { label: 'Select component', value: 'none' },
                { label: 'Text', value: componentTypes.TEXT },
                { label: 'Image', value: componentTypes.IMAGE },
                { label: 'YouTube', value: componentTypes.YOUTUBE },
                { label: 'MP4 Video', value: componentTypes.MP4VIDEO },
                { label: 'Spotify', value: componentTypes.SPOTIFY },
                { label: 'SoundCloud', value: componentTypes.SOUNDCLOUD },
                { label: 'Button', value: componentTypes.BUTTON },
                {
                  label: 'Bain referral banner',
                  value: componentTypes.BAINREFERRALBANNER,
                },
                {
                  label: 'Advertiser Disclosure',
                  value: componentTypes.ADVERTISERDISCLOSURE,
                },
                { label: 'Accordion', value: componentTypes.ACCORDION },
              ])
            : [],
          ...textInputConfig,
          ...imageInputConfig,
          ...buttonInputConfig,
          ...youtubeInputConfig,
          ...mp4VideoInputConfig,
          ...spotifyInputConfig,
          ...soundCloudInputConfig,
          ...bainReferralBannerConfig,
          ...advertiserDisclosureConfig,
          ...accordionConfig,
        ];
      }),
    ]),
  ];
};

const mapDispatchToProps = (dispatch) => ({
  updateComponentProps: (componentData) => {
    dispatch(updateComponent(componentData));
  },
});

export default compose(
  withRouter,
  connect(
    createStructuredSelector({
      size: (state) => state?.mediaQuery,
      selectedComponent: ({ builder }) =>
        builder?.flatOutline?.[builder?.selected], // get Editor Panel current component data
    }),
    mapDispatchToProps
  )
)(Grid);
